// @flow
import { getAuthToken, LOAD_BIDDER_DATA_REQUEST, LOAD_BIDDER_DATA_SUCCESS, LOG_OUT_BIDDER } from './user';
import { LOGIN_REQUEST, LOGIN_SUCCESS } from './login';
import { PASSWORD_RESET_REQUEST, PASSWORD_RESET_SUCCESS } from './passwordReset';
import cloneDeep from 'lodash/cloneDeep';
import { CONFIRM_PASSWORD_SUCCESS } from './confirmPassword';
import { CREATE_ACCOUNT_REQUEST } from './createAccount';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import { postSetSearchFacetOptionExclusion } from '../api/bidder';

/* Action Types */
export const SET_FACET_EXCLUSION_FAIL = 'la/domain/bidderData/SET_FACET_EXCLUSION_FAIL';
export const SET_FACET_EXCLUSION_REQUEST = 'la/domain/bidderData/SET_FACET_EXCLUSION_REQUEST';
export const SET_FACET_EXCLUSION_SUCCESS = 'la/domain/bidderData/SET_FACET_EXCLUSION_SUCCESS';

/* reducer */
const DEFAULT_STATE = {
    excludedFacets: {
        auctionHouse: [],
    },
    loaded: 0,
    loading: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let time;

    switch (action.type) {
        case CONFIRM_PASSWORD_SUCCESS:
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            time = action.meta.actionTime;
            return {
                ...state,
                excludedFacets: renameSearchExclusionsToFacetIds(action.payload.searchExclusions),
                loaded: time,
                loading: false,
            };
        case CREATE_ACCOUNT_REQUEST:
        case LOAD_BIDDER_DATA_REQUEST:
        case LOGIN_REQUEST:
        case PASSWORD_RESET_REQUEST:
        case SET_FACET_EXCLUSION_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        case SET_FACET_EXCLUSION_FAIL:
            return {
                ...state,
                loading: false,
            };
        case SET_FACET_EXCLUSION_SUCCESS:
            time = action.meta.actionTime;
            return {
                ...state,
                excludedFacets: renameSearchExclusionsToFacetIds(action.payload),
                loaded: time,
                loading: false,
            };
        default:
            return state;
    }
}

// TODO: remove this, and instead update the item api to return correct names

/* HELPERS */
const FACET_ID_LEGEND = {
    houseIds: 'auctionHouse',
};

// Maps facet names returned by the api to facet names used within the application
const renameSearchExclusionsToFacetIds = (searchExclusions = {}) =>
    Object.keys(searchExclusions).reduce(
        (excludedFacets, key) => ({
            ...excludedFacets,
            [FACET_ID_LEGEND[key]]: searchExclusions[key].map(option => option.toString()),
        }),
        {},
    );

// END TODO

export const annotateFacetExclusions = (facets: any[] = [], exclusions: any = {}) =>
    facets.map<any>(facet =>
        !exclusions[facet.id]
            ? facet
            : {
                  ...facet,
                  excludable: true,
                  options: facet.options.map(option => ({
                      ...option,
                      excluded: exclusions[facet.id].includes(option.id),
                  })),
              },
    );

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchExclusions;

export const getUserExcludedFacets = createSelector(
    stateSelector,
    state => state.excludedFacets || cloneDeep(DEFAULT_STATE.excludedFacets),
);

export const getUserExcludedHouses = createSelector(getUserExcludedFacets, facets => {
    const searchExclusionsAsStrings: string[] = facets ? facets.auctionHouse : [];
    const searchExclusions: number[] = searchExclusionsAsStrings.map((exclusion: string) => {
        return parseInt(exclusion, 10);
    });
    return searchExclusions;
});

/* ACTION CREATORS */
export const setSearchExclusion = (facetId: any, optionId: any, exclude: any) => async (
    dispatch: Function,
    getState: Function,
) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    const requestParams = { authToken, deployment, exclude, facetId, optionId };
    try {
        dispatch({
            type: SET_FACET_EXCLUSION_REQUEST,
        });
        const response = await postSetSearchFacetOptionExclusion(requestParams);
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: SET_FACET_EXCLUSION_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error.error_message,
            type: SET_FACET_EXCLUSION_FAIL,
        });
    }
};
